import { IoMdClose } from 'react-icons/io';
import CheckStatusBadge from './shared/CheckStatusBadge';
import DocumentStatusBadge from './shared/DocumentStatusBadge';
import DraweeStatusBadge from './shared/DraweeStatusBadge';
import { formatCurrency, formatDate, formatCpfCnpj } from '../../../utils/collateralFormatters';

const PROCESS_LABELS: Record<string, string> = {
    XML: 'XML', INVOICE_PDF: 'NF PDF', RECEIPT_STUB: 'Canhoto',
    ACCEPTED_BILL: 'Duplicata', PURCHASE_CONTRACT: 'Contrato C/V',
};

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex justify-between items-center py-1.5">
        <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
        <span className="text-xs dark:text-white font-medium text-right max-w-[60%]">{value || '—'}</span>
    </div>
);

interface Props {
    item: any;
    onClose: () => void;
}

const ReceivableItemView = ({ item, onClose }: Props) => {
    if (!item) return null;

    const processItems: Record<string, any> = {};
    (item.processItems || []).forEach((pi: any) => { processItems[pi.processType] = pi; });

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-[420px] h-full bg-white dark:bg-slate-800 shadow-xl overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between z-10">
                    <h3 className="text-sm font-semibold dark:text-white">Recebível — {item.titleNumber}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><IoMdClose size={18} /></button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Dados do Recebível */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Dados do Recebível</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <Field label="Nº NF" value={item.titleNumber} />
                            <Field label="Chave NFe" value={<span className="font-mono text-[10px] break-all">{item.nfeKey}</span>} />
                            <Field label="Parcela" value={item.receivableNumber} />
                            <Field label="Emissão" value={formatDate(item.issueDate)} />
                            <Field label="Vencimento" value={formatDate(item.dueDate)} />
                            <Field label="Valor" value={formatCurrency(item.amount)} />
                            <Field label="Município/UF" value={[item.city, item.state].filter(Boolean).join(' - ') || '—'} />
                        </div>
                    </div>

                    {/* Cedente */}
                    {(item.assignorName || item.assignorTaxId) && (
                        <div>
                            <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Cedente</h4>
                            <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                                <Field label="Nome" value={item.assignorName} />
                                <Field label="CPF/CNPJ" value={formatCpfCnpj(item.assignorTaxId)} />
                            </div>
                        </div>
                    )}

                    {/* Sacado */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Sacado</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <Field label="Nome" value={item.debtorName} />
                            <Field label="CPF/CNPJ" value={formatCpfCnpj(item.debtorDocument)} />
                            <Field label="Status" value={item.draweeStatus ? <DraweeStatusBadge status={item.draweeStatus} statusFormatted={item.draweeStatusFormatted || item.draweeStatus} /> : '—'} />
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Status</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-gray-500">Documento:</span>
                                <DocumentStatusBadge status={item.status || ''} statusFormatted={item.statusFormatted || item.status || ''} />
                            </div>
                            {item.observation && (
                                <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded p-2 mt-2">
                                    {item.observation}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Validações */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Validações</h4>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.keys(PROCESS_LABELS).map(p => {
                                const pi = processItems[p];
                                return (
                                    <div key={p} className="p-3 bg-gray-50 dark:bg-slate-900 rounded border border-gray-200 dark:border-slate-700">
                                        <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase mb-1">{PROCESS_LABELS[p]}</div>
                                        {pi ? (
                                            <div className="space-y-0.5">
                                                <Field label="Recebido" value={pi.received ? 'Sim' : 'Não'} />
                                                <Field label="Status" value={<CheckStatusBadge status={pi.status} statusFormatted={pi.statusFormatted || pi.status} />} />
                                                {pi.observation && (
                                                    <div className="text-[11px] text-orange-700 dark:text-orange-300 bg-amber-50 dark:bg-amber-900/20 rounded p-1.5 mt-1">
                                                        {pi.observation}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">Não configurado</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReceivableItemView;
